import { PDFFont, PDFPage, rgb } from "pdf-lib";

export type TextAlignment = "center" | "justify" | "left" | "right";

/**
 * Normalizes Unicode text to WinAnsi-compatible characters for WinAnsi (Windows-1252) encoding.
 * Handles characters that cannot be encoded in WinAnsi (e.g., Turkish "ı" U+0131) by replacing or removing them.
 *
 * @param text - The text to normalize
 * @returns Normalized text with Unicode characters replaced by WinAnsi-compatible equivalents
 */
export const normalizeTextForWinAnsi = (text: string): string => {
  if (!text) {
    return text;
  }

  // Character mapping for common problematic Unicode characters that are NOT in WinAnsi
  // Maps Unicode characters to their WinAnsi-compatible equivalents
  // Note: Characters in the 0x80-0xFF range (like ç, ö, ü) are in WinAnsi and don't need mapping
  const charMap: Record<string, string> = {
    // Turkish characters outside WinAnsi range
    "\u0131": "i", // LATIN SMALL LETTER DOTLESS I (U+0131) - NOT in WinAnsi
    "\u0130": "I", // LATIN CAPITAL LETTER I WITH DOT ABOVE (U+0130) - NOT in WinAnsi
    "\u011F": "g", // LATIN SMALL LETTER G WITH BREVE (U+011F) - NOT in WinAnsi
    "\u011E": "G", // LATIN CAPITAL LETTER G WITH BREVE (U+011E) - NOT in WinAnsi
    "\u015F": "s", // LATIN SMALL LETTER S WITH CEDILLA (U+015F) - NOT in WinAnsi
    "\u015E": "S", // LATIN CAPITAL LETTER S WITH CEDILLA (U+015E) - NOT in WinAnsi
    // Other common problematic characters outside WinAnsi
    "\u2018": "'", // LEFT SINGLE QUOTATION MARK
    "\u2019": "'", // RIGHT SINGLE QUOTATION MARK
    "\u201C": '"', // LEFT DOUBLE QUOTATION MARK
    "\u201D": '"', // RIGHT DOUBLE QUOTATION MARK
    "\u2013": "-", // EN DASH
    "\u2014": "-", // EM DASH
    "\u2026": "...", // HORIZONTAL ELLIPSIS
  };

  /**
   * Checks if a character is supported by WinAnsi (Windows-1252) encoding.
   * WinAnsi supports code points 0x00-0xFF, with printable characters in ranges 0x20-0x7E and 0xA0-0xFF.
   *
   * @param char - The character to check
   * @returns True if the character is supported by WinAnsi encoding
   */
  const isWinAnsi = (char: string): boolean => {
    const code = char.codePointAt(0) ?? 0;
    // Printable ASCII (0x20-0x7E)
    if (code >= 0x20 && code <= 0x7e) {
      return true;
    }
    // Printable WinAnsi (Windows-1252) extended range (0xA0-0xFF)
    if (code >= 0xa0 && code <= 0xff) {
      return true;
    }
    // Some control characters (tab, CR, LF) are also allowed
    if (code === 0x09 || code === 0x0a || code === 0x0d) {
      return true;
    }
    return false;
  };

  let result = "";
  for (const character of text) {
    // If character is in charMap, use mapped value
    if (charMap[character]) {
      result += charMap[character];
      continue;
    }

    // If character is WinAnsi, keep as is
    if (isWinAnsi(character)) {
      result += character;
      continue;
    }

    // Try to decompose and remove diacritics (NFD), then keep base if it's WinAnsi
    const decomposed = character.normalize("NFD").replaceAll(/[\u0300-\u036F]/g, "");
    if (decomposed && isWinAnsi(decomposed)) {
      result += decomposed;
      continue;
    }

    // Fallback: replace with '?'
    result += "?";
  }

  return result;
};

export interface TextDrawingOptions {
  x: number;
  y: number;
  maxWidth: number;
  fontSize: number;
  font: PDFFont;
  lineHeight: number;
  align: TextAlignment;
}

export interface WrappedTextOptions {
  x: number;
  y: number;
  maxWidth: number;
  fontSize: number;
  font: PDFFont;
  lineHeight: number;
}

// Helper function to break a long word into chunks that fit within maxCharsPerLine
const breakLongWord = (word: string, maxCharsPerLine: number): string[] => {
  const chunks: string[] = [];
  for (let i = 0; i < word.length; i += maxCharsPerLine) {
    chunks.push(word.substring(i, i + maxCharsPerLine));
  }
  return chunks;
};

// Helper function to break a long word and add chunks to lines array, returning the new currentLine
export const breakLongWordAndAddToLines = (word: string, maxCharsPerLine: number, lines: string[]): string => {
  // Handle edge cases: empty word or invalid maxCharsPerLine
  if (!word || maxCharsPerLine <= 0) {
    return "";
  }

  const chunks = breakLongWord(word, maxCharsPerLine);

  // If no chunks were created, return empty string
  if (chunks.length === 0) {
    return "";
  }

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    if (chunk) {
      if (i === chunks.length - 1) {
        return chunk;
      } else {
        lines.push(chunk);
      }
    }
  }
  return "";
};

// Helper function to wrap text into lines
export const wrapTextIntoLines = (text: string, maxCharsPerLine: number, isAlreadyNormalized = false): string[] => {
  // Normalize text to handle Unicode characters that can't be encoded in WinAnsi
  const normalizedText = isAlreadyNormalized ? text : normalizeTextForWinAnsi(text);
  const words = normalizedText.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else if (currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      // Single word is too long, break it into chunks
      currentLine = breakLongWordAndAddToLines(word, maxCharsPerLine, lines);
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

// Internal helper to draw text without normalization (assumes text is already normalized)
const drawTextLineInternal = (page: PDFPage, normalizedText: string, x: number, y: number, fontSize: number, font: PDFFont): void => {
  page.drawText(normalizedText, {
    x,
    y,
    font,
    size: fontSize,
    color: rgb(0, 0, 0),
  });
};

// Helper function to draw a single line of text
// Normalizes text to handle Unicode characters that can't be encoded in WinAnsi
export const drawTextLine = (page: PDFPage, text: string, x: number, y: number, fontSize: number, font: PDFFont): void => {
  const normalizedText = normalizeTextForWinAnsi(text);
  drawTextLineInternal(page, normalizedText, x, y, fontSize, font);
};

// Helper function to calculate text X position based on alignment
export const calculateTextX = (x: number, maxWidth: number, text: string, fontSize: number, font: PDFFont, align: string): number => {
  if (align === "center") {
    const textWidth = font.widthOfTextAtSize(text, fontSize);
    return x + (maxWidth - textWidth) / 2;
  }
  if (align === "right") {
    const textWidth = font.widthOfTextAtSize(text, fontSize);
    return x + maxWidth - textWidth;
  }
  return x;
};

export const drawWrappedTextWithAlignment = (page: PDFPage, text: string, options: TextDrawingOptions): number => {
  const { x, y, maxWidth, fontSize, font, lineHeight, align } = options;
  // Normalize text to handle Unicode characters that can't be encoded in WinAnsi
  const normalizedText = normalizeTextForWinAnsi(text);
  const avgCharWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor(maxWidth / avgCharWidth);

  if (normalizedText.length <= maxCharsPerLine) {
    // Text fits on one line - handle alignment
    // Use normalizedText for width calculation and drawing (already normalized, so use internal helper)
    const textX = calculateTextX(x, maxWidth, normalizedText, fontSize, font, align);
    drawTextLineInternal(page, normalizedText, textX, y, fontSize, font);
    return lineHeight;
  }

  // Text needs to be wrapped - pass already normalized text to avoid double normalization
  const lines = wrapTextIntoLines(normalizedText, maxCharsPerLine, true);

  // Draw each line with proper alignment
  // Lines from wrapTextIntoLines are already normalized, so use internal helper to avoid double normalization
  let currentY = y;
  for (const line of lines) {
    const textX = calculateTextX(x, maxWidth, line, fontSize, font, align);
    drawTextLineInternal(page, line, textX, currentY, fontSize, font);
    currentY -= lineHeight;
  }

  return lines.length * lineHeight;
};

export const drawWrappedText = (page: PDFPage, text: string, options: WrappedTextOptions): number => {
  const { x, y, maxWidth, fontSize, font, lineHeight } = options;
  // Normalize text to handle Unicode characters that can't be encoded in WinAnsi
  const normalizedText = normalizeTextForWinAnsi(text);
  const averageCharWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor(maxWidth / averageCharWidth);

  if (normalizedText.length <= maxCharsPerLine) {
    // Text fits on one line
    page.drawText(normalizedText, {
      x,
      y,
      font,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
    return lineHeight;
  }

  // Text needs to be wrapped - pass already normalized text to avoid double normalization
  const lines = wrapTextIntoLines(normalizedText, maxCharsPerLine, true);

  // Draw each line
  for (const [index, line] of lines.entries()) {
    page.drawText(line, {
      x,
      y: y - index * lineHeight,
      font,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  }

  return lines.length * lineHeight;
};

export const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
  // wrapTextIntoLines normalizes internally, so we don't need to normalize here
  // Handle empty string case - wrapTextIntoLines returns [] for empty, but wrapText should return [""]
  if (!text) {
    return [""];
  }
  const averageCharWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor(maxWidth / averageCharWidth);

  return wrapTextIntoLines(text, maxCharsPerLine);
};
